import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { CreateTaskFieldKey, CreateTaskFieldValue, CreateTaskState } from '@/domains/tasks/types/createTaskSliceTypes';
import { SelectOption } from '@/features/editMode/types/selectOption';
import { createTaskInitialState } from '@/domains/tasks/constants/createTaskSliceConstants';

const createTaskSlice = createSlice({
  name: 'createTask',
  initialState: createTaskInitialState,
  reducers: {
    createTask: () => {},
    updateCreateTaskStepValues: <K extends CreateTaskFieldKey>(
      state: CreateTaskState,
      action: PayloadAction<{ step: K; value: CreateTaskFieldValue<K> }>,
    ) => {
      const { step, value } = action.payload;
      state.createTaskSteps[step] = value;
    },
    resetCreateTaskState: () => createTaskInitialState,
    setCreateTaskLoadingStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.statuses.isCreateTaskLoading = payload;
    },
    getCreateTaskClaims: (_, _action: PayloadAction<string>) => {},
    getCreateTaskClaimsSuccess: (state, { payload }: PayloadAction<SelectOption[]>) => {
      state.claimOptions = payload;
    },
    getCreateTaskClaimants: (_, _action: PayloadAction<string>) => {},
    getCreateTaskClaimantsSuccess: (state, { payload }: PayloadAction<SelectOption[]>) => {
      state.claimantOptions = payload;
    },
    setCreateTaskOptionsLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.statuses.isOptionsLoading = payload;
    },
    getTaskTypeOptions: () => {},
    getTaskTypeOptionsSuccess: (
      state,
      { payload: { listings } }: PayloadAction<{ listings: NormalizedV2Response[] }>,
    ) => {
      state.taskTypes = listings.map((listing) => ({
        label: listing.fields.name?.value?.toString() || '',
        value: listing.id.toString(),
        entityTypeId: listing.fields.entityTypeId?.value as number,
      }));
    },
  },
  selectors: {
    selectCreateTaskSteps: (state) => state.createTaskSteps,
    selectCreateTaskIsLoading: (state) => state.statuses.isCreateTaskLoading,
    selectCreateTaskClaimOptions: (state) => state.claimOptions,
    selectCreateTaskClaimantOptions: (state) => state.claimantOptions,
    selectTaskTypeOptions: (state) => state.taskTypes,
    selectCreateTaskOptionsLoading: (state) => state.statuses.isOptionsLoading,
  },
});

export const {
  updateCreateTaskStepValues,
  resetCreateTaskState,
  setCreateTaskLoadingStatus,
  createTask,
  getCreateTaskClaims,
  getCreateTaskClaimsSuccess,
  setCreateTaskOptionsLoading,
  getCreateTaskClaimants,
  getCreateTaskClaimantsSuccess,
  getTaskTypeOptions,
  getTaskTypeOptionsSuccess,
} = createTaskSlice.actions;

export const {
  selectCreateTaskSteps,
  selectCreateTaskIsLoading,
  selectCreateTaskClaimOptions,
  selectCreateTaskOptionsLoading,
  selectCreateTaskClaimantOptions,
  selectTaskTypeOptions,
} = createTaskSlice.selectors;

export const createTaskReducer = createTaskSlice.reducer;
export const createTaskReducerPath = createTaskSlice.reducerPath;
